// StudentTaxCalculator.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

/** ---------------- i18n-ish messages (student flavor) ---------------- */
const MESSAGES = {
    en_US: {
        headings: {
            main: "Tax Calculator",
            result: "Result",
        },
        labels: {
            itemCost: "Item cost",
            country: "Your country",
            detected: "Detected",
        },
        buttons: {
            useLocation: "Use my location",
            compute: "Compute taxes",
            computing: "Computing…",
        },
        notices: {
            missingProps: "Missing backEndUrl, tenantId, or itemCost.",
            failedDetect: "Could not determine your country automatically. Please select it.",
            failedCompute: "Failed to compute taxes.",
        },
        result: {
            original: "Original amount",
            tax: "Tax amount",
            total: "Total with tax",
            rate: "Tax rate applied",
        },
    },
    en_AU: {},
};

const getMessages = (loc) => ({ ...MESSAGES.en_US, ...(MESSAGES[loc] || {}) });

/** ---------- Backend API adapter (tenant-aware) ---------- */
function makeBackendApi(backEndUrl, tenantId) {
    const base = (backEndUrl || "").replace(/\/+$/, "");
    const http = axios.create({
        baseURL: base,
        timeout: 25000,
        headers: {
            Accept: "application/json",
            ...(tenantId ? { "x-tenant-id": tenantId } : {}),
        },
    });

    return {
        computeTaxes: async ({ country, amount }) => {
            const { data } = await http.post(`/catalog-taxes/compute`, { country, amount });
            return data || {};
        },
    };
}

/** ---------- Helpers ---------- */
const parseAmount = (v) => {
    if (v === null || v === undefined) return NaN;
    const n = typeof v === "number" ? v : Number(String(v).trim());
    return Number.isFinite(n) ? n : NaN;
};

const normalizeCountryCode = (v) => {
    const s = String(v || "").trim();
    if (!s) return "";
    return s.toUpperCase();
};

const toIntlLocale = (loc) => String(loc || "en_US").replace("_", "-");

// Try to infer country from locale like "en-US" -> "US"
const getCountryFromLocale = () => {
    try {
        const lang = (navigator.language || "").trim();
        if (!lang) return "";
        if (typeof Intl !== "undefined" && typeof Intl.Locale === "function") {
            const loc = new Intl.Locale(lang);
            return normalizeCountryCode(loc.region || "");
        }
        const parts = lang.split("-");
        return normalizeCountryCode(parts[1] || "");
    } catch {
        return "";
    }
};

// Optional: use geolocation + reverse-geocode to countryCode (best-effort)
async function getCountryFromGeolocation({ timeoutMs = 8000 } = {}) {
    if (!navigator.geolocation) return "";

    const pos = await new Promise((resolve, reject) => {
        const t = setTimeout(() => reject(new Error("geolocation timeout")), timeoutMs);
        navigator.geolocation.getCurrentPosition(
            (p) => {
                clearTimeout(t);
                resolve(p);
            },
            (e) => {
                clearTimeout(t);
                reject(e);
            },
            { enableHighAccuracy: false, maximumAge: 60_000, timeout: timeoutMs }
        );
    }).catch(() => null);

    if (!pos?.coords) return "";

    const { latitude, longitude } = pos.coords;

    const ctrl = new AbortController();
    const t2 = setTimeout(() => ctrl.abort(), timeoutMs);

    try {
        const url =
            `https://api.bigdatacloud.net/data/reverse-geocode-client` +
            `?latitude=${encodeURIComponent(latitude)}` +
            `&longitude=${encodeURIComponent(longitude)}` +
            `&localityLanguage=en`;

        const resp = await fetch(url, { signal: ctrl.signal });
        if (!resp.ok) return "";
        const json = await resp.json();
        return normalizeCountryCode(json?.countryCode || "");
    } catch {
        return "";
    } finally {
        clearTimeout(t2);
    }
}

const safeCurrencyFormatter = (locale, currencyCode) => {
    const intlLoc = toIntlLocale(locale);
    const cur = String(currencyCode || "USD").trim().toUpperCase() || "USD";

    try {
        if (typeof Intl === "undefined" || typeof Intl.NumberFormat !== "function") {
            return null;
        }
        return new Intl.NumberFormat(intlLoc, {
            style: "currency",
            currency: cur,
            currencyDisplay: "symbol",
            maximumFractionDigits: 2,
        });
    } catch {
        // currencyCode might be invalid; try a safe fallback currency
        try {
            return new Intl.NumberFormat(intlLoc, {
                style: "currency",
                currency: "USD",
                currencyDisplay: "symbol",
                maximumFractionDigits: 2,
            });
        } catch {
            return null;
        }
    }
};

const formatMoney = (formatter, value) => {
    const n = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(n)) return "-";
    if (formatter) return formatter.format(n);
    // last-resort fallback
    return `${Math.round(n * 100) / 100}`;
};

/** ---------- Flash message ---------- */
const Flash = ({ type, children }) => {
    if (!children) return null;
    const styles = {
        marginBottom: 6,
        padding: "8px 10px",
        borderRadius: 6,
        border: "1px solid",
        background: type === "error" ? "#ffecec" : "#ecfff1",
        borderColor: type === "error" ? "#ffb3b3" : "#b3ffcc",
        color: type === "error" ? "#a40000" : "#005c2e",
        fontSize: 12,
    };
    return (
        <div role={type === "error" ? "alert" : "status"} style={styles} aria-live="polite">
            {children}
        </div>
    );
};

/** ---------- Compact UI CSS ---------- */
const compactCss = `
  .bb-container { font-size: 13px; padding: 8px 10px; background: #f5f5f7; }
  .bb-card { background: #fff; }
  .bb-card h2 { font-size: 16px; font-weight: 700 !important; margin: 0; }
  .bb-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .bb-muted { color: #555; font-size: 12px; }
  .bb-field { padding: 8px; border-radius: 6px; border: 1px solid #eee; }
  .bb-label { display:block; font-size: 12px; font-weight: 600; margin-bottom: 4px; }
  select, input { font-size: 12px; padding: 4px 6px; height: 30px; font-weight: 400; color: inherit; }
  .bb-card .bb-button,
  .bb-container .bb-button {
    -webkit-appearance: none !important;
    appearance: none !important;
    background: #f6f6f6 !important;
    border: 1px solid #d3d3d3 !important;
    color: #222 !important;
    border-radius: 6px !important;
    padding: 6px 10px !important;
    font: inherit !important;
    line-height: 1.2 !important;
    cursor: pointer !important;
  }
  .bb-card .bb-button:hover,
  .bb-container .bb-button:hover { background: #efefef !important; }
  .bb-card .bb-button:focus-visible { outline: 2px solid #5a9cff !important; outline-offset: 2px !important; }
`;

/** ---------- Component ---------- */
const StudentTaxCalculator = ({
                                  backEndUrl,
                                  tenantId,
                                  itemCost,
                                  currencyCode = "USD",
                                  autoCompute = false,
                                  locale = "en_US",
                              }) => {
    const M = useMemo(() => getMessages(locale), [locale]);
    const backend = useMemo(() => makeBackendApi(backEndUrl, tenantId), [backEndUrl, tenantId]);

    const moneyFmt = useMemo(
        () => safeCurrencyFormatter(locale, currencyCode),
        [locale, currencyCode]
    );

    const [country, setCountry] = useState("");
    const [detecting, setDetecting] = useState(false);

    const [computing, setComputing] = useState(false);
    const [fatalError, setFatalError] = useState("");
    const [inlineError, setInlineError] = useState("");
    const [result, setResult] = useState(null);

    const amountNum = useMemo(() => parseAmount(itemCost), [itemCost]);
    const canRun = Boolean(backEndUrl && tenantId) && Number.isFinite(amountNum);

    async function detectCountryBestEffort() {
        setInlineError("");
        setDetecting(true);
        try {
            let code = await getCountryFromGeolocation({ timeoutMs: 8000 });
            if (!code) code = getCountryFromLocale();
            setCountry(code || "");
            if (!code) setInlineError(M.notices.failedDetect);
            return code || "";
        } finally {
            setDetecting(false);
        }
    }

    async function computeNow(countryCode) {
        setInlineError("");
        setResult(null);

        const c = normalizeCountryCode(countryCode || country);
        if (!c) {
            setInlineError(M.notices.failedDetect);
            return;
        }
        if (!canRun) {
            setInlineError(M.notices.missingProps);
            return;
        }

        setComputing(true);
        try {
            const data = await backend.computeTaxes({ country: c, amount: amountNum });
            setResult(data);
        } catch (err) {
            setInlineError(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                M.notices.failedCompute
            );
        } finally {
            setComputing(false);
        }
    }

    // Auto mode: detect location + compute on mount (and when tenant/backEnd/itemCost changes)
    useEffect(() => {
        let cancelled = false;

        (async () => {
            setFatalError("");
            setInlineError("");
            setResult(null);

            if (!autoCompute) return;

            if (!canRun) {
                if (!cancelled) setFatalError(M.notices.missingProps);
                return;
            }

            const code = await detectCountryBestEffort();
            if (cancelled) return;

            if (code) {
                await computeNow(code);
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoCompute, canRun, backEndUrl, tenantId, itemCost, currencyCode, locale]);

    if (!canRun && !autoCompute) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                    <Flash type="error">{M.notices.missingProps}</Flash>
                </div>
            </div>
        );
    }

    if (fatalError) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                    <Flash type="error">{fatalError}</Flash>
                </div>
            </div>
        );
    }

    // Prefer backend amounts if present; otherwise use prop
    const shownOriginal =
        typeof result?.originalAmount === "number" ? result.originalAmount : amountNum;

    return (
        <div className="bb-container" style={{ display: "grid", gap: 12 }}>
            <style dangerouslySetInnerHTML={{ __html: compactCss }} />

            <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                <div className="bb-row" style={{ justifyContent: "space-between" }}>
                    <h2>{M.headings.main}</h2>

                    {/* Hide all buttons when autoCompute=true */}
                    {!autoCompute && (
                        <div className="bb-row">
                            <button
                                type="button"
                                className="bb-button"
                                onClick={detectCountryBestEffort}
                                disabled={detecting || computing}
                            >
                                {M.buttons.useLocation}
                            </button>
                            <button
                                type="button"
                                className="bb-button"
                                onClick={() => computeNow()}
                                disabled={computing || detecting || !country}
                                aria-disabled={computing || detecting || !country}
                            >
                                {computing ? M.buttons.computing : M.buttons.compute}
                            </button>
                        </div>
                    )}
                </div>

                <Flash type="error">{inlineError}</Flash>

                <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                    <div className="bb-field">
                        <span className="bb-label">{M.labels.itemCost}</span>
                        <div style={{ fontSize: 13 }}>
                            {Number.isFinite(amountNum) ? formatMoney(moneyFmt, amountNum) : String(itemCost || "")}
                            <span className="bb-muted" style={{ marginLeft: 8 }}>
                ({String(currencyCode || "USD").toUpperCase()})
              </span>
                        </div>
                    </div>

                    <div className="bb-field">
                        <label className="bb-label" htmlFor="countryInput">
                            {M.labels.country}
                            {autoCompute && country ? (
                                <span className="bb-muted" style={{ marginLeft: 8 }}>
                  ({M.labels.detected})
                </span>
                            ) : null}
                        </label>

                        <input
                            id="countryInput"
                            type="text"
                            value={country}
                            onChange={(e) => setCountry(normalizeCountryCode(e.target.value))}
                            placeholder="US"
                            style={{ width: 120 }}
                            disabled={computing || detecting}
                        />

                        {autoCompute && (detecting || computing) ? (
                            <div className="bb-muted" style={{ marginTop: 6 }}>
                                {computing ? M.buttons.computing : "Detecting location…"}
                            </div>
                        ) : null}
                    </div>

                    {autoCompute ? (
                        <AutoRecompute
                            country={country}
                            computing={computing}
                            onCompute={() => computeNow(country)}
                        />
                    ) : null}

                    {result ? (
                        <div className="bb-field" style={{ borderColor: "#ddd" }}>
                            <div className="bb-row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
                <span className="bb-label" style={{ margin: 0 }}>
                  {M.headings.result}
                </span>
                                {result?.country ? <span className="bb-muted">{String(result.country)}</span> : null}
                            </div>

                            <div style={{ display: "grid", gap: 6, fontSize: 13 }}>
                                <div>
                                    <strong>{M.result.original}:</strong>{" "}
                                    {formatMoney(moneyFmt, shownOriginal)}
                                </div>
                                <div>
                                    <strong>{M.result.tax}:</strong>{" "}
                                    {formatMoney(moneyFmt, result.taxAmount)}
                                </div>
                                <div>
                                    <strong>{M.result.total}:</strong>{" "}
                                    {formatMoney(moneyFmt, result.newAmount)}
                                </div>
                                <div className="bb-muted">
                                    <strong>{M.result.rate}:</strong>{" "}
                                    {typeof result.taxRateApplied === "number"
                                        ? `${Math.round(result.taxRateApplied * 10000) / 100}%`
                                        : String(result.taxRateApplied ?? "-")}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

// Recompute when country changes in auto mode (simple debounce)
const AutoRecompute = ({ country, computing, onCompute }) => {
    useEffect(() => {
        if (!country) return;
        let cancelled = false;
        const t = setTimeout(() => {
            if (!cancelled && !computing) onCompute();
        }, 500);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [country]);
    return null;
};

export default StudentTaxCalculator;